import React, { useState, useEffect, useMemo } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import numberToWords from 'number-to-words';
import { Row, Col, Button } from 'reactstrap';
import PropTypes from 'prop-types';

import FormInput from '../../components/form/FormInput';
import FormSelect from '../../components/form/FormSelect';
import FormTextarea from '../../components/form/FormTextarea';
import FormDateInput from '../../components/form/FormDateInput';
import FormNumberInput from '../../components/form/FormNumberInput';
import { useTranslation } from 'react-i18next';

const Fields = ({ data = [], onUpdate }) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    const initialValues = data.reduce((acc, field) => {
      acc[field.field_rank] = field.crm_value;
      return acc;
    }, {});
    setFormValues(initialValues);
  }, [data]);

  useEffect(() => {
    const formattedFields = data.map((field) => {
      const value = formValues[field.field_rank];
      let formattedValue = value;

      if (field.field_type === 'date' && value) {
        const parsedDate = typeof value === 'string' ? parseISO(value) : new Date(value);
        if (isValid(parsedDate)) {
          formattedValue = format(parsedDate, 'yyyy-MM-dd');
        }
      }

      return {
        field_rank: field.field_rank,
        crm_value: formattedValue
      };
    });

    onUpdate({ fields: formattedFields });
  }, [formValues, onUpdate, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => {
      const updatedValues = { ...prevState, [name]: value || '' };
      return updatedValues;
    });
  };

  const renderField = (field) => {
    const {
      field_rank,
      field_type,
      drop_down_option,
      editable_field,
      label_name,
      is_required
    } = field;
    const isDisabled = editable_field === 'false';
    const displayLabel =
      is_required === true || is_required === 'true' ? (
        <span>
          {label_name} <span className="text-danger">*</span>
        </span>
      ) : (
        label_name
      );

    switch (field_type) {
      case 'text': {
        return (
          <FormInput
            name={field_rank}
            value={formValues[field_rank]}
            label={displayLabel}
            disabled={isDisabled}
            onChange={handleChange}
          />
        );
      }
      case 'date': {
        return (
          <FormDateInput
            name={field_rank}
            dateFormat="dd/MM/yyyy"
            value={formValues[field_rank]}
            label={displayLabel}
            disabled={isDisabled}
            onChange={handleChange}
          />
        );
      }
      case 'select': {
        const options = drop_down_option.split(',').map((option) => ({
          value: option.trim(),
          label: option.trim()
        }));
        return (
          <FormSelect
            name={field_rank}
            value={formValues[field_rank]}
            label={displayLabel}
            options={options}
            disabled={isDisabled}
            onChange={handleChange}
          />
        );
      }
      case 'number': {
        const numberValue = formValues[field_rank];

        const numberInWords = useMemo(() => {
          if (numberValue !== null && numberValue !== undefined && numberValue !== '') {
            const num = Number(numberValue);
            return isNaN(num) ? '' : numberToWords.toWords(num);
          }
          return '';
        }, [numberValue]);

        return (
          <div>
            <FormNumberInput
              name={field_rank}
              value={numberValue}
              label={displayLabel}
              disabled={isDisabled}
              onChange={handleChange}
            />
            {numberInWords && (
              <div style={{ marginTop: '4px', fontSize: '0.875rem', color: '#666' }}>
                {numberInWords}
              </div>
            )}
          </div>
        );
      }
      case 'textarea': {
        return (
          <FormTextarea
            name={field_rank}
            value={formValues[field_rank]}
            label={displayLabel}
            disabled={isDisabled}
            onChange={handleChange}
            rows={1}
          />
        );
      }
      case 'button': {
        const urlValue = formValues[field_rank] || '';
        return (
          <Button
            color="primary"
            disabled={isDisabled}
            className="w-100 px-5 py-2 bg-primary border-primary btn btn-secondary"
            onClick={() => {
              if (urlValue) {
                const url = urlValue.startsWith('http') ? urlValue : `https://${urlValue}`;
                window.open(url, '_blank');
              }
            }}>
            {displayLabel}
          </Button>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <div className="fs-4 fw-bolder py-2">{t('CRMFields')}</div>
      <Row xs="3" className="w-100">
        {data.map((field) => (
          <Col key={field.field_rank}>{renderField(field)}</Col>
        ))}
      </Row>
    </>
  );
};

Fields.propTypes = {
  data: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default Fields;
